import React from 'react'
import dynamic from 'next/dynamic'
import { Insight, InsightComponentProps } from 'src/components/insights/types'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { ZoningIcon } from 'src/components/insights/icons/zoning'
import { Colors } from 'src/components/theme'
import InsightLocked from 'src/components/insight-locked'
import { useTier } from 'src/hooks/use-tier'

import zoningPreview1 from 'src/assets/images/insights-previews/zoning/zoning_1.gif'
import zoningPreview1Placeholder from 'src/assets/images/insights-previews/zoning/zoning_1.png'
import zoningPreview2 from 'src/assets/images/insights-previews/zoning/zoning_2.gif'
import zoningPreview2Placeholder from 'src/assets/images/insights-previews/zoning/zoning_2.png'
import zoningPreview3 from 'src/assets/images/insights-previews/zoning/zoning_3.gif'
import zoningPreview3Placeholder from 'src/assets/images/insights-previews/zoning/zoning_3.png'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'
import { InsightContainer } from 'src/v2/views/owner-reveal/insights/container'

const ZoningContent = dynamic(() => import('src/components/insights/zoning/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const ZoningPage: React.FC<InsightComponentProps> = (props) => {
  const { insight } = props
  const meetsTier = useTier(INSIGHTS_TIER['Zoning'])

  return (
    <InsightContainer insight={insight} color={Colors.orangeDark}>
      <React.Suspense fallback={<FullScreenLoading />}>
        {!meetsTier ? <InsightLocked insight={insight} /> : <ZoningContent />}
      </React.Suspense>
    </InsightContainer>
  )
}

export const Zoning: Insight = {
  id: 'zoning',
  component: ZoningPage,
  name: 'Zoning',
  icon: ZoningIcon,
  supportedResultTypes: ['venue', 'address', 'alphamap-location'],
  linkTooltipFile: 'insights-zoning',
  tier: INSIGHTS_TIER['Zoning'],
  paywall: {
    title: 'See Zoning Reports in Action',
    description: `Zoning isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
    synopsis: [
      {
        image: zoningPreview1.src,
        imagePlaceholder: zoningPreview1Placeholder.src,
        title: 'Know Your Zoning',
        description:
          'Instantly identify property zoning classifications, unlocking development possibilities and land use options.',
      },
      {
        image: zoningPreview2.src,
        imagePlaceholder: zoningPreview2Placeholder.src,
        title: 'Regulation Clarity',
        description:
          'Easily navigate local zoning laws, ensuring compliance and strategic planning for property development.',
      },
      {
        image: zoningPreview3.src,
        imagePlaceholder: zoningPreview3Placeholder.src,
        title: 'Maximize Development Potential',
        description:
          "Assess and amplify a property's development potential, reducing risks and guiding investment choices.",
      },
    ],
  },
}
